import React, { useState, useEffect } from "react";
import DeleteModal from "components/Common/DeleteModal";
import {
  getCountryEvents,
  getPolicyUpdates,
  getPolicyUpdatePicture,
  getPolicyUpdateUserPicture,
  editCountryEvent,
  editPolicyUpdateForm,
  deleteCountryEvent,
  deletePolicyUpdate
} from "services/policyUpdatesAndEventsService"
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input, 
  UncontrolledTooltip
} from "reactstrap"
import SearchBox from "components/Common/SearchBox";
import Pagination from "components/Common/Pagination";
import AddUpdateOrEventButton from "./AddUpdateOrEventButton";
import { formatDistanceToNow } from "date-fns";
import { listCountries } from "../../services"

const PolicyUpdates = ({alert, externalCountryID=null, showAdd=true}) => {
  const [updateOffset, setUpdateOffset] = useState(0)
  const [events, setEvents] = useState([]);
  const [policyUpdates, setPolicyUpdates] = useState([]);
  const [totalEventEntries, setTotalEventEntries] = useState(0);
  const [totalUpdateEntries, setTotalUpdateEntries] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [countries, setCountries] = useState([]);
  const [policyUpdatePictures, setPolicyUpdatePictures] = useState({});
  const [userPictures, setUserPictures] = useState({});

  const usePublic = !showAdd

  // Modal states for events
  const [editEvent, setEditEvent] = useState(null);
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);

  const initialPolicyUpdate = {
    name: "",
    description: "",
    source: "",
    photo: null,
  }

  // Modal states for policy updates
  const [editPolicyUpdate, setEditPolicyUpdate] = useState(initialPolicyUpdate);
  const [isPolicyUpdateModalOpen, setIsPolicyUpdateModalOpen] = useState(false);

  // Delete modal states
  const [deleteModal, setDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState(""); // "event" or "policyUpdate"

  const updateLimit = 10;
  const eventLimit = 3;

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const countriesResponse = await listCountries();
        setCountries(countriesResponse);
      } catch (err) {
        console.error("Error fetching countries:", err);
      }
    };

    fetchCountries();
  }, []);

  const getCountryName = (countryId) => {
    const country = countries.find((c) => c.id === countryId);
    return country ? country.long_name : "Unknown";
  };

  // Function to load events - now independent of pagination
  const loadEvents = async (query) => {
    try {
      // Load events with a fixed limit, regardless of pagination
      const eventQuery = { 
        ...query, 
        limit: eventLimit, 
        offset: 0, // Always start from beginning for events
        countryId: externalCountryID,
        usePublic
      };
      const data = await getCountryEvents(eventQuery);
      setTotalEventEntries(data.length);
      setEvents(data);
    } catch (err) {
      console.error("Error fetching events:", err);
      setEvents([]);
    }
  };

  // Function to load policy updates and their pictures
  const loadPolicyUpdates = async (query) => {
    try {
      const { data, count } = await getPolicyUpdates(query);
      setTotalUpdateEntries(count);
      setPolicyUpdates(data);

      // Load pictures for each policy update and user
      data.forEach((update) => {
        loadPolicyUpdatePicture(update.id);
        const userIdToLoad = update?.updated_by_id || update?.created_by_id;
        loadUserPicture(userIdToLoad);
      });
    } catch (err) {
      console.error("Error fetching policy updates:", err);
      setPolicyUpdates([]);
    }
  };

  const loadPolicyUpdatePicture = async (policy_update_id) => {
    try {
      const picture = await getPolicyUpdatePicture(policy_update_id);
      const pictureUrl = URL.createObjectURL(picture);
      setPolicyUpdatePictures((prevPictures) => ({
        ...prevPictures,
        [policy_update_id]: pictureUrl,
      }));
    } catch (err) {
      console.error("Error fetching policy update picture:", err);
    }
  };

  const loadUserPicture = async (user_id) => {
    try {
      const picture = await getPolicyUpdateUserPicture(user_id);
      const pictureUrl = URL.createObjectURL(picture);
      setUserPictures((prevPictures) => ({
        ...prevPictures,
        [user_id]: pictureUrl,
      }));
    } catch (err) {
      console.error("Error fetching user picture:", err);
    }
  };

  const formatDateToInput = (dateString) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Function to refresh the events list
  const refreshEvents = () => {
    loadEvents({ limit: eventLimit, offset: 0, countryId: externalCountryID, usePublic, searchTerm });
  };

  // Function to refresh the policy updates list
  const refreshPolicyUpdates = () => {
    loadPolicyUpdates({ limit: updateLimit, offset: updateOffset, countryId: externalCountryID, usePublic, searchTerm });
  };

  const handlePageChange = pageNum => {
    const updateOffset = (pageNum - 1) * updateLimit;
    setCurrentPage(pageNum);
    setUpdateOffset(updateOffset);
    // Only update policy updates when page changes, events stay the same
    loadPolicyUpdates({ 
      limit: updateLimit, 
      offset: updateOffset, 
      searchTerm,
      countryId: externalCountryID,
      usePublic
    });
  };

  // Open modal for editing event
  const openEditEventModal = (event) => {
    setEditEvent(event);
    setIsEventModalOpen(true);
  };

  // Open modal for editing policy update
  const openEditPolicyUpdateModal = (update) => {
    setEditPolicyUpdate(update);
    setIsPolicyUpdateModalOpen(true);
  };

  // Open delete modal
  const openDeleteModal = (item, type) => {
    setItemToDelete(item);
    setDeleteType(type);
    setDeleteModal(true);
  };

  // Handle delete action
  const handleDelete = async () => {
    try {
      if (deleteType === "event") {
        await deleteCountryEvent(itemToDelete.id);
        alert("Country Event Deletion", true)
        refreshEvents();
      } else if (deleteType === "policyUpdate") {
        await deletePolicyUpdate(itemToDelete.id);
        alert("Policy Update Deletion", true)
        refreshPolicyUpdates();
      }
      setDeleteModal(false);
      setItemToDelete(null);
    } catch (err) {
      console.error("Error deleting item:", err);
    }
  };

  const handleSearchInputChange = (event) => {
    setSearchTerm(event.target.value);
    loadPolicyUpdates({ limit: updateLimit, offset: updateOffset, searchTerm: event.target.value });
  };

  useEffect(() => {
    refreshEvents();
    refreshPolicyUpdates();
  }, [searchTerm, externalCountryID, updateOffset]);

  // Event and Policy Update Modals and Save Logic
  const handleEventInputChange = (e) => {
    const { name, value } = e.target;
    setEditEvent((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePolicyUpdateInputChange = (e) => {
    if (e.target.name === "photo") {
      setEditPolicyUpdate({...editPolicyUpdate, photo: e.target.files[0]});
    } else {
      setEditPolicyUpdate({...editPolicyUpdate, [e.target.name]: e.target.value});
    }
  };

  const handleSaveEvent = async () => {
    try {
      const formattedEventData = {
        ...editEvent,
        start_date: new Date(editEvent.start_date).toISOString(),
        end_date: new Date(editEvent.end_date).toISOString(),
      };

      await editCountryEvent(editEvent.id, formattedEventData);
      alert("Country Event Updation", true)
      setIsEventModalOpen(false);
      refreshEvents();
    } catch (error) {
      console.error("Error saving the event:", error);
    }
  };

  const handleSavePolicyUpdate = async () => {
    try {
      const formData = new FormData();

      const policyUpdateBody = {
        name: editPolicyUpdate.name,
        description: editPolicyUpdate.description,
        source: editPolicyUpdate.source,
      };

      formData.append("policy_update_update_request_body", JSON.stringify(policyUpdateBody));

      const photo = editPolicyUpdate.photo
      if (photo && photo instanceof File) {
        formData.append("photo", photo);
      }

      await editPolicyUpdateForm(editPolicyUpdate.id, formData);
      alert("Policy Update Updation", true)
      setIsPolicyUpdateModalOpen(false);
      refreshPolicyUpdates();

    } catch (error) {
      console.error("Error saving the policy update:", error);
    }
  };

  return (
    <div className={`flex flex-col gap-2 ${showAdd ? 'p-1' : 'p-0'} max-w-7xl mx-auto`}>
      {/* Header Section - Minimal spacing */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-1 md:gap-1">
        <div className="w-full md:max-w-md">
          <SearchBox searchChange={handleSearchInputChange} />
        </div>
        {showAdd && (
          <div className="w-full md:w-auto mt-1 md:mt-0">
            <AddUpdateOrEventButton alert={alert} refreshEvents={refreshEvents} refreshPolicyUpdates={refreshPolicyUpdates} />
          </div>
        )}
      </div>

      {/* Content Container - Minimal gap */}
      <div className="flex flex-col lg:flex-row gap-2 mt-0">
        {/* Left Content - Policy Updates */}
        <div className="w-full lg:w-2/3">
          <div className="bg-white rounded-lg shadow p-2">
            <h3 className="text-lg font-semibold text-green-700 mb-2 pb-2 border-b border-gray-200">
              Policy Updates
            </h3>

            {policyUpdates.length > 0 ? (
              policyUpdates.map((update) => {
                const lastUpdatedDate = update.updated_at
                  ? new Date(update.updated_at)
                  : new Date(update.created_at);

                const userIdToShow = update.updated_by_id || update.created_by_id;

                return (
                  <div key={update.id} className="flex flex-col sm:flex-row gap-3 mb-4 pb-4 border-b border-gray-200">
                    <img
                      src={policyUpdatePictures[update.id] || update.photo}
                      alt={update.name}
                      className="w-full sm:w-24 h-auto sm:h-24 object-cover rounded-lg flex-shrink-0"
                    />
                    <div className="flex flex-col flex-grow">
                      <h4 className="text-green-800 text-base font-medium mb-2">{update.name}</h4>
                      <p className="text-sm text-gray-700 mb-2">{update.description}</p>
                      <p className="text-sm text-gray-600 mb-3">
                        Source: {" "}
                        <a href={update.source} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline break-words">
                          {update.source}
                        </a>
                      </p>
                      <div className="flex justify-between items-center flex-wrap mt-1">
                        {(
                          <div className="flex items-center gap-2">
                            <img
                              src={userPictures[userIdToShow] || "path/to/default/profile-photo.jpg"}
                              alt={`${update.created_by.first_name} ${update.created_by.last_name}`}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                            <div className="flex flex-col">
                              <span className="text-xs font-semibold text-gray-800">
                                {`${update.created_by.first_name} ${update.created_by.last_name}`}
                              </span>
                              <span className="text-xs text-gray-600">
                                {formatDistanceToNow(lastUpdatedDate, { addSuffix: true })}
                              </span>
                            </div>
                          </div>
                        )}
                        {showAdd && (
                          <div className="flex gap-3 mt-2 sm:mt-0">
                            <button 
                              className="p-1 rounded hover:bg-gray-100 transition-colors" 
                              onClick={() => openEditPolicyUpdateModal(update)}
                            >
                              <i className="fas fa-edit text-green-700" id={`updateedittooltip-${update.id}`}></i>
                              <UncontrolledTooltip placement="top" target={`updateedittooltip-${update.id}`}>
                                Edit
                              </UncontrolledTooltip>
                            </button>
                            <button 
                              className="p-1 rounded hover:bg-gray-100 transition-colors"
                              onClick={() => openDeleteModal(update, "policyUpdate")}
                            >
                              <i className="mdi mdi-delete text-lg text-red-600" id={`updatedeletetooltip-${update.id}`} />
                              <UncontrolledTooltip placement="top" target={`updatedeletetooltip-${update.id}`}>
                                Delete
                              </UncontrolledTooltip>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="text-center text-gray-500 italic bg-gray-50 p-5 rounded-md">No updates found</p>
            )}
            
            {/* Pagination for policy updates */}
            <div className="mt-4">
              <Pagination
                currentPage={currentPage}
                goToPage={handlePageChange}
                totalEntries={totalUpdateEntries}
                entriesPerPage={updateLimit}
              />
            </div>
          </div>
        </div>

        {/* Right Content - Events */}
        <div className="w-full lg:w-1/3">
          <div className="bg-white rounded-lg shadow p-2">
            <h3 className="text-lg font-semibold text-green-800 mb-2 pb-2 border-b border-gray-200 flex items-center gap-2">
              <i className="fas fa-calendar-alt"></i> Upcoming Events
            </h3>

            {events.length > 0 ? (
              events.map((event) => (
                <div key={event.id} className="relative mb-2 pb-2 border-b border-gray-200 last:border-0">
                  <div className="flex">
                    <div className="bg-green-50 p-2 rounded-lg text-center mr-3 flex-shrink-0 w-[70px] h-[70px] flex flex-col justify-center items-center">
                      <span className="text-xs text-green-700 uppercase font-medium">
                        {new Date(event.start_date).toLocaleString("default", { month: "short" })}
                      </span>
                      <span className="text-2xl font-bold text-green-700 leading-tight">
                        {new Date(event.start_date).getDate()}
                      </span>
                      <span className="text-xs text-green-700">
                        {new Date(event.start_date).getFullYear()}
                      </span>
                    </div>
                    
                    <div className="flex-grow pr-0 sm:pr-10 whitespace-normal">
                      <h5 className="text-green-800 text-base font-medium mb-1">{event.name}</h5>
                      <p className="text-sm text-gray-700 mb-1 flex items-start">
                        <span className="w-5 text-green-700 mr-1 flex-shrink-0">
                          <i className="fas fa-map-marker-alt"></i>
                        </span>
                        <span className="whitespace-normal">Venue: {event.venue}</span>
                      </p>
                      <p className="text-sm text-gray-700 mb-1 flex items-start">
                        <span className="w-5 text-green-700 mr-1 flex-shrink-0">
                          <i className="fas fa-flag"></i>
                        </span>
                        <span className="whitespace-normal">Country: {getCountryName(event.country_id)}</span>
                      </p>
                      <p className="text-sm text-gray-700 mb-2 flex items-start">
                        <span className="w-5 text-green-700 mr-1 flex-shrink-0">
                          <i className="fas fa-calendar-day"></i>
                        </span>
                        <span className="whitespace-normal">
                          Dates: {" "}
                          {new Date(event.start_date).toLocaleDateString("en-GB", {
                            day: "numeric",
                            month: "short"
                          })} {" "}
                          - {" "}
                          {new Date(event.end_date).toLocaleDateString("en-GB", {
                            day: "numeric",
                            month: "short"
                          })}
                        </span>
                      </p>
                      <span className="inline-block bg-gray-200 px-2 py-1 rounded-full text-xs">
                        {event.event_type}
                      </span>
                    </div>
                  </div>

                  {showAdd && (
                    <div className="absolute top-0 right-0 sm:flex flex-col gap-2 hidden">
                      <button className="p-1 rounded hover:bg-gray-100 transition-colors" onClick={() => openEditEventModal(event)}>
                        <i className="fas fa-edit text-green-700" id={`eventedittooltip-${event.id}`}></i>
                        <UncontrolledTooltip placement="top" target={`eventedittooltip-${event.id}`}>
                          Edit
                        </UncontrolledTooltip>
                      </button>
                      <button className="p-1 rounded hover:bg-gray-100 transition-colors" onClick={() => openDeleteModal(event, "event")}>
                        <i className="mdi mdi-delete text-lg text-red-600" id={`eventdeletetooltip-${event.id}`} />
                        <UncontrolledTooltip placement="top" target={`eventdeletetooltip-${event.id}`}>
                          Delete
                        </UncontrolledTooltip>
                      </button>
                    </div>
                  )}
                  
                  {showAdd && (
                    <div className="flex justify-end gap-3 mt-2 sm:hidden">
                      <button className="p-1 rounded hover:bg-gray-100 transition-colors" onClick={() => openEditEventModal(event)}>
                        <i className="fas fa-edit text-green-700" id={`eventedittooltip-mobile-${event.id}`}></i>
                        <UncontrolledTooltip placement="top" target={`eventedittooltip-mobile-${event.id}`}>
                          Edit
                        </UncontrolledTooltip>
                      </button>
                      <button className="p-1 rounded hover:bg-gray-100 transition-colors" onClick={() => openDeleteModal(event, "event")}>
                        <i className="mdi mdi-delete text-lg text-red-600" id={`eventdeletetooltip-mobile-${event.id}`} />
                        <UncontrolledTooltip placement="top" target={`eventdeletetooltip-mobile-${event.id}`}>
                          Delete
                        </UncontrolledTooltip>
                      </button>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <p className="text-center text-gray-500 italic bg-gray-50 p-5 rounded-md">No events found</p>
            )}
          </div>
        </div>
      </div>

      {/* No global pagination anymore - moved to updates section */}

      {/* Event Modal */}
      {editEvent && (
        <Modal
          isOpen={isEventModalOpen}
          toggle={() => setIsEventModalOpen(!isEventModalOpen)}
          className="max-w-lg mx-auto"
        >
          <ModalHeader toggle={() => setIsEventModalOpen(!isEventModalOpen)} 
                      className="bg-white text-green-800 border-b-0 text-center text-xl font-medium">
            Edit Event
          </ModalHeader>

          <div className="text-left mx-6 my-1 text-gray-600 text-xs italic">
            <small>All fields marked with asterisks (<span className="text-red-500">*</span>) are compulsory.</small>
          </div>

          <ModalBody className="p-6">
            <Form>
              <FormGroup>
                <Label for="eventName" className="text-green-800 font-semibold mb-2 block">
                  Event Name<span className="text-red-500">*</span>
                </Label>
                <Input
                  type="text"
                  name="name"
                  id="eventName"
                  value={editEvent.name}
                  onChange={handleEventInputChange}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 mb-4"
                />
              </FormGroup>
              <FormGroup>
                <Label for="eventVenue" className="text-green-800 font-semibold mb-2 block">
                  Venue<span className="text-red-500">*</span>
                </Label>
                <Input
                  type="text"
                  name="venue"
                  id="eventVenue"
                  value={editEvent.venue}
                  onChange={handleEventInputChange}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 mb-4"
                />
              </FormGroup>
              <FormGroup>
                <Label for="eventStartDate" className="text-green-800 font-semibold mb-2 block">
                  Start Date<span className="text-red-500">*</span>
                </Label>
                <Input
                  type="date"
                  name="start_date"
                  id="eventStartDate"
                  value={formatDateToInput(editEvent.start_date)}
                  onChange={handleEventInputChange}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 mb-4"
                />
              </FormGroup>
              <FormGroup>
                <Label for="eventEndDate" className="text-green-800 font-semibold mb-2 block">
                  End Date<span className="text-red-500">*</span>
                </Label>
                <Input
                  type="date"
                  name="end_date"
                  id="eventEndDate"
                  value={formatDateToInput(editEvent.end_date)}
                  onChange={handleEventInputChange}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 mb-4"
                />
              </FormGroup>
              <FormGroup>
                <Label for="eventType" className="text-green-800 font-semibold mb-2 block">
                  Event Type<span className="text-red-500">*</span>
                </Label>
                <Input
                  type="text"
                  name="event_type"
                  id="eventType"
                  value={editEvent.event_type}
                  onChange={handleEventInputChange}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 mb-4"
                />
              </FormGroup>
            </Form>
          </ModalBody>
          <ModalFooter className="border-t-0 p-4 flex justify-center">
            <Button
              style={{
                backgroundColor: '#036F36',
                color: '#FFF605',
                borderRadius: '2.25rem',
                width: '100%',
              }}
              onClick={handleSaveEvent}
            >
              Save Changes
            </Button>
          </ModalFooter>
        </Modal>
      )}

      {/* Policy Update Modal */}
      {editPolicyUpdate && (
        <Modal
          isOpen={isPolicyUpdateModalOpen}
          toggle={() => setIsPolicyUpdateModalOpen(!isPolicyUpdateModalOpen)}
          className="max-w-lg mx-auto"
        >
          <ModalHeader toggle={() => setIsPolicyUpdateModalOpen(!isPolicyUpdateModalOpen)} 
                      className="bg-white text-green-800 border-b-0 text-center text-xl font-medium">
            Edit Policy Update
          </ModalHeader>

          <div className="text-left mx-6 my-1 text-gray-600 text-xs italic">
            <small>All fields marked with asterisks (<span className="text-red-500">*</span>) are compulsory.</small>
          </div>

          <ModalBody className="p-6">
            <Form>
              <FormGroup>
                <Label for="policyUpdateName" className="text-green-800 font-semibold mb-2 block">
                  Update Name<span className="text-red-500">*</span>
                </Label>
                <Input
                  type="text"
                  name="name"
                  id="policyUpdateName"
                  value={editPolicyUpdate.name}
                  onChange={handlePolicyUpdateInputChange}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 mb-4"
                />
              </FormGroup>
              <FormGroup>
                <Label for="policyUpdateDescription" className="text-green-800 font-semibold mb-2 block">
                  Description<span className="text-red-500">*</span>
                </Label>
                <Input
                  type="textarea"
                  name="description"
                  id="policyUpdateDescription"
                  value={editPolicyUpdate.description}
                  onChange={handlePolicyUpdateInputChange}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 mb-4"
                />
              </FormGroup>
              <FormGroup>
                <Label for="policyUpdateSource" className="text-green-800 font-semibold mb-2 block">
                  Source<span className="text-red-500">*</span>
                </Label>
                <Input
                  type="url"
                  name="source"
                  id="policyUpdateSource"
                  value={editPolicyUpdate.source}
                  onChange={handlePolicyUpdateInputChange}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 mb-4"
                />
              </FormGroup>
              <FormGroup>
                <Label for="policyUpdatePhoto" className="text-green-800 font-semibold mb-2 block">
                  Upload Photo
                </Label>
                <Input
                  type="file"
                  name="photo"
                  className="input-field w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500 mb-4"
                  id="policyUpdatePhoto"
                  onChange={handlePolicyUpdateInputChange}
                />
              </FormGroup>
            </Form>
          </ModalBody>
          <ModalFooter className="border-t-0 p-4 flex justify-center">
            <Button
              style={{
                backgroundColor: '#036F36',
                color: '#FFF605',
                borderRadius: '2.25rem',
                width: '100%',
              }}
              onClick={handleSavePolicyUpdate}
            >
              Save Changes
            </Button>
          </ModalFooter>
        </Modal>
      )}

      {/* Delete Modal */}
      {deleteModal && (
        <DeleteModal
          show={deleteModal}
          onDeleteClick={handleDelete}
          onCloseClick={() => setDeleteModal(false)}
          item={deleteType}
        />
      )}
    </div>
  );
};

export default PolicyUpdates;